/**
 * Asset manifest resolution (WR.3.2). The descriptor-driven scene builder
 * (WR.3.3) uses this to turn a biome's `assetPack` reference into resolvable
 * renderable artifacts; the loader (loader.ts) turns a resolved `AssetEntry`
 * into a Three.js object. All of it is presentation-only and off the per-tick
 * path (wr3-asset-pipeline-standup.md §4) — collision stays the WR.2 shared
 * source (AD-047), and no wire field changes.
 *
 * Import boundary (AD-122): the client reads content types via the curated
 * `@great-cloud/shared/client` surface only, never the server barrel. These
 * imports are type-only, so this module stays framework-free and node-safe (the
 * unit tests run under `node --test` with no renderer).
 */

import type {
  AssetEmitter,
  AssetEntry,
  AssetPackDef,
  WorldDescriptor,
} from '@great-cloud/shared/client';

/**
 * A read-only index of an asset pack's entries by id. An UNBOUND manifest (no
 * pack, or a descriptor that names no `assetPack`) resolves nothing, which is how
 * the loader and scene builder fall back to placeholder primitives (the
 * graceful-degradation rule, §8).
 */
export class AssetManifest {
  private readonly entries: Map<string, AssetEntry>;
  /** The pack's runtime emitters by asset id (q-npc-props-adoption-and-flame). */
  private readonly emitters: Readonly<Record<string, readonly AssetEmitter[]>>;
  /** The bound pack id, or `null` when no pack is bound (placeholder-fallback mode). */
  readonly packId: string | null;

  constructor(pack?: AssetPackDef | null) {
    this.packId = pack?.id ?? null;
    this.entries = new Map();
    this.emitters = pack?.emitters ?? {};
    for (const entry of pack?.assets ?? []) {
      // The build-time manifest tool already guarantees kebab + uniqueness
      // (build-asset-manifest.mjs); the Map is belt-and-braces, last-write-wins.
      this.entries.set(entry.id, entry);
    }
  }

  /** Whether a pack is bound. `false` ⇒ every `resolve()` is a deliberate fallback. */
  get bound(): boolean {
    return this.packId !== null;
  }

  /** Number of indexed entries. */
  get size(): number {
    return this.entries.size;
  }

  /** Resolve an entry by id, or `undefined` (unknown id / unbound ⇒ fallback). */
  resolve(id: string): AssetEntry | undefined {
    return this.entries.get(id);
  }

  /** The runtime emitters an asset declares (a flame), or undefined when it declares none. */
  emittersFor(id: string): readonly AssetEmitter[] | undefined {
    const list = Object.prototype.hasOwnProperty.call(this.emitters, id)
      ? this.emitters[id]
      : undefined;
    return list !== undefined && list.length > 0 ? list : undefined;
  }

  /** Whether an id is present in the bound pack. */
  has(id: string): boolean {
    return this.entries.has(id);
  }

  /** The indexed ids in pack order (the build tool sorts entries by id). */
  ids(): string[] {
    return [...this.entries.keys()];
  }
}

/**
 * Pure: pick the `AssetPackDef` a descriptor binds, from packs indexed by id
 * (the client content pack carries the `asset-packs` category). Returns
 * `undefined` when the descriptor names no pack or the named pack is absent —
 * both are graceful fallbacks, never an error (the descriptor is server-authored
 * and a pack may simply not be delivered yet).
 */
export function selectAssetPack(
  descriptor: Pick<WorldDescriptor, 'assetPack'>,
  packsById: ReadonlyMap<string, AssetPackDef>,
): AssetPackDef | undefined {
  const name = descriptor.assetPack;
  if (name === undefined || name === '') return undefined;
  return packsById.get(name);
}

/**
 * Pure: build the delivery URL for a content-addressed handle (an entry `url` or
 * a `lods[]` LOD handle) from the static asset HTTP base (net/platform.ts
 * `assetHttpBase()` — `''` same-origin by default, or a configured CDN/static
 * origin), appending the content `hash` as a cache-busting query (mirrors the
 * content-pack hash mechanism, §6). The LOD variants of one entry share its
 * `hash` — adequate cache-busting, since a new bake restamps the whole entry.
 */
export function deliveryUrl(handle: string, hash: string | undefined, httpBase = ''): string {
  const base = httpBase.replace(/\/+$/, '');
  const path = handle.replace(/^\/+/, '');
  const url = base === '' ? `/${path}` : `${base}/${path}`;
  return hash ? `${url}?v=${encodeURIComponent(hash)}` : url;
}

/**
 * Pure: the delivery URL for an entry's base `url` (the LOD-0 / no-LOD path).
 * Returns `null` when the entry carries no `url` (⇒ fallback). The LOD-aware path
 * (`lodHandle` + `deliveryUrl`) lives in the loader.
 */
export function assetUrl(entry: AssetEntry, httpBase = ''): string | null {
  if (entry.url === undefined || entry.url === '') return null;
  return deliveryUrl(entry.url, entry.hash, httpBase);
}
